import React from "react";
import {
  TableRow as StyledTableRow,
  CheckboxColumn,
  Checkbox,
  ActionButtonColumn,
  RowActionButton,
  ImagesColumn,
  CardImages,
  ImageContainer,
  CardThumbnail,
  ImageLoader,
  TitleColumn,
  OcrStatusColumn,
  RowStatusSelect,
  SuggestedPriceColumn,
  CategoryColumn,
  YearColumn,
  SetColumn,
  CardNameColumn,
  IdColumn,
  BatchColumn,
  UploadedAtColumn,
  DescriptionColumn,
  ConditionColumn,
  ProducerColumn,
  RarityColumn,
  SubSetColumn,
  EditionColumn,
  VariantColumn,
  LanguageColumn,
  AutographColumn,
  CardNumberColumn,
  MarketValueColumn,
  GradeColumn,
  DamageColumn,
  GradedColumn,
  SpecialFeaturesColumn,
  SellingPointsColumn,
  CardGameTypeColumn,
  EbayTitleColumn,
  EbayCategoryColumn,
  EbayConditionColumn,
  EbayDescriptionColumn,
  CountryColumn,
  ManufacturerColumn,
  YearManufacturedColumn,
  VintageColumn,
  ConditionDescriptionColumn,
  AvailableQuantityColumn,
  TableCell,
  CellContent,
  StatusBadge,
  StatusIcon,
  StatusText,
} from "./CardsView.styles";
import { getOcrStatusColor, formatDate, cardHasLongTitle } from "./CardsView.utils";
import { isCardLockedForEdit, canEditListedCardField } from "../../services/ebaySync";

const TableRow = ({
  card,
  columnOrder,
  columnVisibility,
  selectedCards,
  imageLoading,
  editingCell,
  editValue,
  onCardSelect,
  onCellClick,
  onImageClick,
  onImageLoadStart,
  onImageLoad,
  onImageError,
  onStatusUpdate,
  onRefreshCard,
  isRefreshingCard = false,
  onRowClick,
  renderEditableCell,
  batchTitleBuilderConfig,
  isEditModeEnabled = true,
  isHighlighted = false,
  rowRef,
}) => {
  // Helper function to get field value from card - always read from ebay_listing (source of truth)
  const getFieldValueFromCard = (fieldName) => {
    const fieldMapping = {
      "Card Name":
        card.metadata?.ebay_listing?.ItemSpecifics?.["Card Name"] || "",
      Type: card.metadata?.ebay_listing?.ItemSpecifics?.["Type"] || "",
      Manufacturer:
        card.metadata?.ebay_listing?.ItemSpecifics?.["Manufacturer"] || "",
      Set: card.metadata?.ebay_listing?.ItemSpecifics?.["Set"] || "",
      "Card Number":
        card.metadata?.ebay_listing?.ItemSpecifics?.["Card Number"] || "",
      "Year Manufactured":
        card.metadata?.ebay_listing?.ItemSpecifics?.["Year Manufactured"] || "",
    };
    return fieldMapping[fieldName] || "";
  };

  // Format title builder configuration
  const formatTitleBuilderConfig = () => {
    if (
      !batchTitleBuilderConfig ||
      !batchTitleBuilderConfig.configuration ||
      batchTitleBuilderConfig.configuration.length === 0
    ) {
      return (
        card.metadata?.ebay_listing?.Title || card.metadata?.title || "N/A"
      );
    }

    // New format: [{ type: "field-name" | "string", value: string }, ...]
    let title = "";

    batchTitleBuilderConfig.configuration.forEach((item, index) => {
      if (item.type === "field-name") {
        // Get field value from card
        const fieldValue = getFieldValueFromCard(item.value || "");
        if (fieldValue) {
          title += fieldValue;
          // Add a space after the field (default behavior)
          title += " ";
        }
      } else if (item.type === "string") {
        // Add string value directly (preserves spaces)
        title += item.value || "";
      }
    });

    // Trim and return, or fallback to default title
    const finalTitle = title.trim();
    return (
      finalTitle ||
      card.metadata?.ebay_listing?.Title ||
      card.metadata?.title ||
      "N/A"
    );
  };

  // Helper function to check if value should be rendered in red
  const shouldRenderRed = (value) => {
    if (!value) return true;
    const normalizedValue =
      typeof value === "string" ? value.trim().toLowerCase() : "";
    return normalizedValue === "n/a" || normalizedValue === "unknown";
  };

  // Quantity/price stay editable even once a card is listed - saving pushes the
  // change live to eBay via bulkUpdatePriceQuantity instead of just the local DB.
  const isCellLockedForField = (card, fieldName) =>
    isCardLockedForEdit(card) && !canEditListedCardField(card, fieldName);

  // Wrapper function to handle cell clicks with edit mode check
  const handleCellClickWrapper = (card, fieldName, e) => {
    if (!isEditModeEnabled) {
      // Don't stop propagation - let it bubble to row level
      return;
    }
    // Don't allow editing if card is listed or syncing (except quantity/price)
    if (isCellLockedForField(card, fieldName)) {
      e.stopPropagation();
      return;
    }
    onCellClick(card, fieldName, e);
  };
  const hasLongTitle = cardHasLongTitle(card);

  return (
    <StyledTableRow
      key={card.id}
      ref={rowRef}
      $highlighted={isHighlighted}
      onClick={() => onRowClick(card, "basic")}
      style={{
        cursor: "pointer",
        backgroundColor: hasLongTitle ? "#fef2f2" : undefined,
      }}
    >
      <CheckboxColumn $highlighted={isHighlighted} onClick={(e) => e.stopPropagation()}>
        <Checkbox
          type="checkbox"
          checked={selectedCards.has(card.id)}
          disabled={isCardLockedForEdit(card)}
          onChange={(e) => {
            e.stopPropagation();
            if (isCardLockedForEdit(card)) {
              return;
            }
            onCardSelect(card.id, e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
          style={{
            cursor: isCardLockedForEdit(card) ? "not-allowed" : "pointer",
            opacity: isCardLockedForEdit(card) ? 0.5 : 1,
          }}
        />
      </CheckboxColumn>
      <ActionButtonColumn onClick={(e) => e.stopPropagation()}>
        <RowActionButton
          type="button"
          aria-label="Refresh row"
          disabled={isRefreshingCard}
          onClick={(e) => {
            e.stopPropagation();
            if (onRefreshCard) {
              onRefreshCard(card.id);
            }
          }}
        >
          {isRefreshingCard ? "..." : "↻"}
        </RowActionButton>
      </ActionButtonColumn>

      {columnOrder.map((columnKey) => {
        if (!columnVisibility[columnKey]) return null;

        switch (columnKey) {
          case "images":
            return (
              <ImagesColumn key={columnKey}>
                <CardImages>
                  <ImageContainer>
                    {imageLoading[`${card.id}-front`] ? (
                      <ImageLoader />
                    ) : (
                      <CardThumbnail
                        src={card.front_image}
                        alt={`Card ${card.id} front`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onImageClick(card, card.front_image);
                        }}
                        onLoadStart={() => onImageLoadStart(card.id, "front")}
                        onLoad={() => onImageLoad(card.id, "front")}
                        onError={() => onImageError(card.id, "front")}
                        style={{ cursor: "pointer" }}
                      />
                    )}
                  </ImageContainer>
                  <ImageContainer>
                    {imageLoading[`${card.id}-back`] ? (
                      <ImageLoader />
                    ) : (
                      <CardThumbnail
                        src={card.back_image}
                        alt={`Card ${card.id} back`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onImageClick(card, card.back_image);
                        }}
                        onLoadStart={() => onImageLoadStart(card.id, "back")}
                        onLoad={() => onImageLoad(card.id, "back")}
                        onError={() => onImageError(card.id, "back")}
                        style={{ cursor: "pointer" }}
                      />
                    )}
                  </ImageContainer>
                </CardImages>
              </ImagesColumn>
            );

          case "status": {
            const isLocked = isCardLockedForEdit(card);
            const isListing = card.status === "listing";
            return (
              <OcrStatusColumn
                key={columnKey}
                onClick={(e) => e.stopPropagation()}
              >
                <RowStatusSelect
                  value={card.status || "draft"}
                  onChange={(e) => {
                    if (
                      e.target.value === "ready" &&
                      cardHasLongTitle(card)
                    ) {
                      return;
                    }
                    onStatusUpdate(card.id, e.target.value);
                  }}
                  disabled={!isEditModeEnabled || isLocked}
                  title={
                    hasLongTitle
                      ? "Title exceeds 80 characters — shorten before marking ready"
                      : isListing
                        ? "Card is being listed on eBay"
                        : undefined
                  }
                  style={{
                    cursor:
                      isEditModeEnabled && !isLocked
                        ? "pointer"
                        : "not-allowed",
                    opacity: isEditModeEnabled && !isLocked ? 1 : 0.6,
                  }}
                >
                  {card.status === "listed" && (
                    <option value="listed">Listed</option>
                  )}
                  {isListing && (
                    <option value="listing">Listing…</option>
                  )}
                  <option value="ready" disabled={hasLongTitle}>
                    Ready
                  </option>
                  <option value="draft">Draft</option>
                </RowStatusSelect>
                {card.ebay_sync_status && card.ebay_sync_status !== "not_synced" && (
                  <span
                    style={{
                      display: "block",
                      marginTop: "0.25rem",
                      textAlign: "center",
                      fontSize: "0.6875rem",
                      color:
                        card.ebay_sync_status === "failed"
                          ? "#b91c1c"
                          : card.ebay_sync_status === "needs_resync"
                            ? "#b45309"
                            : card.ebay_sync_status === "synced"
                              ? "#047857"
                              : "#2563eb",
                    }}
                    title={card.last_sync_error || undefined}
                  >
                    eBay: {card.ebay_sync_status.replace(/_/g, " ")}
                  </span>
                )}
              </OcrStatusColumn>
            );
          }

          case "sale_price":
            return (
              <SuggestedPriceColumn
                key={columnKey}
                onClick={(e) => handleCellClickWrapper(card, "sale_price", e)}
                style={{
                  cursor:
                    isEditModeEnabled && !isCellLockedForField(card, "sale_price")
                      ? "pointer"
                      : "default",
                }}
              >
                {renderEditableCell(
                  card,
                  "sale_price",
                  card.metadata?.ebay_listing?.SalePrice
                )}
              </SuggestedPriceColumn>
            );

          case "ebay_title":
            // ebay_title is editable - check isTitleEdited flag
            // If isTitleEdited is true, use metadata.ebay_listing.Title
            // Otherwise, use formatTitleBuilderConfig()
            const isTitleEdited = card.isTitleEdited === true;
            const ebayTitleValue = isTitleEdited 
              ? (card.metadata?.ebay_listing?.Title || formatTitleBuilderConfig())
              : formatTitleBuilderConfig();
            const isEbayTitleNA = shouldRenderRed(ebayTitleValue);

            return (
              <EbayTitleColumn
                key={columnKey}
                onClick={(e) => handleCellClickWrapper(card, "ebay_title", e)}
                style={{
                  cursor:
                    isEditModeEnabled && !isCardLockedForEdit(card)
                      ? "pointer"
                      : "default",
                  position: "relative",
                  ...(hasLongTitle
                    ? {
                        boxShadow: "inset 0 0 0 2px #ef4444",
                      }
                    : {}),
                }}
              >
                {isTitleEdited && (
                  <span
                    style={{
                      position: "absolute",
                      top: "6px",
                      right: "8px",
                      display: "inline-flex",
                      alignItems: "center",
                      background: "#dbeafe",
                      color: "#1d4ed8",
                      fontSize: "0.6875rem",
                      fontWeight: "600",
                      padding: "0.125rem 0.5rem",
                      borderRadius: "999px",
                      zIndex: 10,
                    }}
                  >
                    Edited
                  </span>
                )}
                <span
                  style={{
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    paddingRight: isTitleEdited ? "3.5rem" : 0,
                  }}
                >
                  {renderEditableCell(
                    card,
                    "ebay_title",
                    ebayTitleValue
                  )}
                </span>
              </EbayTitleColumn>
            );

          case "ebay_category":
            return (
              <EbayCategoryColumn
                key={columnKey}
                onClick={(e) =>
                  handleCellClickWrapper(card, "ebay_category", e)
                }
              >
                {renderEditableCell(
                  card,
                  "category",
                  card.metadata?.category || "N/A"
                )}
              </EbayCategoryColumn>
            );

          case "ebay_condition":
            return (
              <EbayConditionColumn
                key={columnKey}
                onClick={(e) =>
                  handleCellClickWrapper(card, "ebay_condition", e)
                }
              >
                {renderEditableCell(
                  card,
                  "condition",
                  card.metadata?.condition || "N/A"
                )}
              </EbayConditionColumn>
            );

          case "ebay_description":
            return (
              <EbayDescriptionColumn
                key={columnKey}
                onClick={(e) =>
                  handleCellClickWrapper(card, "ebay_description", e)
                }
              >
                {renderEditableCell(
                  card,
                  "ebay_listing.Description",
                  card.metadata?.ebay_listing?.Description,
                  true
                )}
              </EbayDescriptionColumn>
            );

          case "ebay_card_name":
            return (
              <CardNameColumn
                key={columnKey}
                onClick={(e) =>
                  handleCellClickWrapper(card, "ebay_card_name", e)
                }
              >
                {renderEditableCell(
                  card,
                  "ebay_listing.ItemSpecifics.Card Name",
                  card.metadata?.ebay_listing?.ItemSpecifics?.["Card Name"] ||
                    "N/A"
                )}
              </CardNameColumn>
            );

          case "ebay_set":
            return (
              <SetColumn
                key={columnKey}
                onClick={(e) => handleCellClickWrapper(card, "ebay_set", e)}
              >
                {renderEditableCell(
                  card,
                  "ebay_listing.ItemSpecifics.Set",
                  card.metadata?.ebay_listing?.ItemSpecifics?.["Set"] || "N/A"
                )}
              </SetColumn>
            );

          case "ebay_country":
            return (
              <CountryColumn
                key={columnKey}
                onClick={(e) => handleCellClickWrapper(card, "ebay_country", e)}
              >
                {renderEditableCell(
                  card,
                  "ebay_listing.ItemSpecifics.Country of Origin",
                  card.metadata?.ebay_listing?.ItemSpecifics?.[
                    "Country of Origin"
                  ] || "N/A"
                )}
              </CountryColumn>
            );

          case "ebay_language":
            return (
              <LanguageColumn
                key={columnKey}
                onClick={(e) =>
                  handleCellClickWrapper(card, "ebay_language", e)
                }
              >
                {renderEditableCell(
                  card,
                  "ebay_listing.ItemSpecifics.Language",
                  card.metadata?.ebay_listing?.ItemSpecifics?.["Language"] ||
                    "N/A"
                )}
              </LanguageColumn>
            );

          case "ebay_manufacturer":
            return (
              <ManufacturerColumn
                key={columnKey}
                onClick={(e) =>
                  handleCellClickWrapper(card, "ebay_manufacturer", e)
                }
              >
                {renderEditableCell(
                  card,
                  "ebay_listing.ItemSpecifics.Manufacturer",
                  card.metadata?.ebay_listing?.ItemSpecifics?.[
                    "Manufacturer"
                  ] ||
                    card.metadata?.ebay_listing?.ItemSpecifics?.["Brand"] ||
                    "N/A"
                )}
              </ManufacturerColumn>
            );

          case "ebay_autographed":
            return (
              <AutographColumn
                key={columnKey}
                onClick={(e) =>
                  handleCellClickWrapper(card, "ebay_autographed", e)
                }
              >
                {renderEditableCell(
                  card,
                  "ebay_listing.ItemSpecifics.Autographed",
                  card.metadata?.ebay_listing?.ItemSpecifics?.["Autographed"] ||
                    "N/A"
                )}
              </AutographColumn>
            );

          case "ebay_card_number":
            return (
              <CardNumberColumn
                key={columnKey}
                onClick={(e) =>
                  handleCellClickWrapper(card, "ebay_card_number", e)
                }
              >
                {renderEditableCell(
                  card,
                  "ebay_listing.ItemSpecifics.Card Number",
                  card.metadata?.ebay_listing?.ItemSpecifics?.["Card Number"] ||
                    "N/A"
                )}
              </CardNumberColumn>
            );

          case "ebay_year_manufactured":
            return (
              <YearManufacturedColumn
                key={columnKey}
                onClick={(e) =>
                  handleCellClickWrapper(card, "ebay_year_manufactured", e)
                }
              >
                {renderEditableCell(
                  card,
                  "ebay_listing.ItemSpecifics.Year Manufactured",
                  card.metadata?.ebay_listing?.ItemSpecifics?.[
                    "Year Manufactured"
                  ] ||
                    card.metadata?.ebay_listing?.ItemSpecifics?.["Year"] ||
                    "N/A"
                )}
              </YearManufacturedColumn>
            );

          case "ebay_graded":
            return (
              <GradedColumn
                key={columnKey}
                onClick={(e) => handleCellClickWrapper(card, "ebay_graded", e)}
              >
                {renderEditableCell(
                  card,
                  "ebay_listing.ItemSpecifics.Graded",
                  card.metadata?.ebay_listing?.ItemSpecifics?.["Graded"] ||
                    "N/A"
                )}
              </GradedColumn>
            );

          case "ebay_vintage":
            return (
              <VintageColumn
                key={columnKey}
                onClick={(e) => handleCellClickWrapper(card, "ebay_vintage", e)}
              >
                {renderEditableCell(
                  card,
                  "ebay_listing.ItemSpecifics.Vintage",
                  card.metadata?.ebay_listing?.ItemSpecifics?.["Vintage"] ||
                    "N/A"
                )}
              </VintageColumn>
            );

          case "ebay_condition_description":
            return (
              <ConditionDescriptionColumn
                key={columnKey}
                onClick={(e) =>
                  onCellClick(card, "ebay_condition_description", e)
                }
              >
                {renderEditableCell(
                  card,
                  "ebay_listing.Condition Description",
                  card.metadata?.ebay_listing?.["Condition Description"] ||
                    "N/A"
                )}
              </ConditionDescriptionColumn>
            );

          case "available_quantity":
            return (
              <AvailableQuantityColumn
                key={columnKey}
                onClick={(e) =>
                  handleCellClickWrapper(card, "available_quantity", e)
                }
                style={{
                  cursor:
                    isEditModeEnabled &&
                    !isCellLockedForField(card, "available_quantity")
                      ? "pointer"
                      : "default",
                }}
              >
                {renderEditableCell(
                  card,
                  "available_quantity",
                  card.metadata?.ebay_listing?.AvailableQuantity ?? ""
                )}
              </AvailableQuantityColumn>
            );

          default:
            return null;
        }
      })}
    </StyledTableRow>
  );
};

export default TableRow;
